/**
 * MTCNClaimPage - Western Union Style Claim Page
 * 
 * Recipients enter their MTCN code + security answer (last name) to claim funds.
 * The code IS the wallet private key - reconstructed in the browser.
 * 
 * Flow:
 * 1. Enter MTCN code (12 words or display code)
 * 2. Enter security answer (recipient's last name)
 * 3. Verify → Show amount
 * 4. Choose cash-out method:
 *    - Option A: External Exchange (available now)
 *    - Option B: Instant Widget (coming soon / when enabled)
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Loader2, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight, 
  Banknote,
  Shield,
  Clock,
  Gift,
  KeyRound,
  User,
  Building2,
  Sparkles,
  ExternalLink,
  Copy,
  Check,
  Lock
} from 'lucide-react';
import CashOutOptions from '../components/CashOutOptions';
import { useGeoLocation, useExchangeRate } from '../hooks/useGeoLocation';
import { OFF_RAMP_WIDGET_ENABLED } from '../config/cashOutConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Off-ramp configuration (for widget mode when enabled)
const TRANSAK_API_KEY = process.env.REACT_APP_TRANSAK_API_KEY || '';

// Flow steps
const STEPS = {
  ENTER_CODE: 'enter_code',
  VERIFYING: 'verifying',
  SHOW_AMOUNT: 'show_amount',
  CASH_OUT_OPTIONS: 'cash_out_options',  // NEW: Dual option selection
  SELECT_METHOD: 'select_method',        // Legacy: Widget provider selection
  OFFRAMP_WIDGET: 'offramp_widget',
  SUCCESS: 'success',
  ERROR: 'error'
};

// Off-ramp providers (used when widget is enabled)
const OFFRAMP_PROVIDERS = [
  {
    id: 'transak',
    name: 'Bank Transfer',
    description: 'Withdraw directly to your bank account',
    time: '1-3 business days',
    available: !!TRANSAK_API_KEY || OFF_RAMP_WIDGET_ENABLED,
    icon: Building2,
    recommended: true
  }
];

export default function MTCNClaimPage() {
  const navigate = useNavigate();
  
  // Geo location and rate hooks
  const { country: userCountry, currency: localCurrency, loading: geoLoading } = useGeoLocation();
  const { rate: exchangeRate, getEstimatedAmount } = useExchangeRate(localCurrency);
  
  // State
  const [step, setStep] = useState(STEPS.ENTER_CODE);
  const [mnemonicInput, setMnemonicInput] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claimData, setClaimData] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleVerifyClaim = async () => {
    if (!mnemonicInput.trim()) {
      setError('Please enter your transfer code');
      return;
    }
    
    if (!securityAnswer.trim()) {
      setError('Please enter the security answer');
      return;
    }
    
    setLoading(true);
    setError(null);
    setStep(STEPS.VERIFYING);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/mtcn/verify-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mnemonic: mnemonicInput.trim(),
          security_answer: securityAnswer.trim()
        })
      });
      
      // Read response body ONCE
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response:', responseText);
        throw new Error('Server returned invalid response');
      }
      
      // Check for errors
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid code or security answer');
      }
      
      if (!data.valid) {
        throw new Error(data.error || 'Invalid claim');
      }
      
      setClaimData(data);
      setStep(STEPS.SHOW_AMOUNT);
      
    } catch (err) {
      console.error('Verify claim error:', err);
      setError(err.message || 'Verification failed. Please try again.');
      setStep(STEPS.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawToBank = () => {
    // NEW: Go to dual cash-out options instead of directly to widget selection
    setStep(STEPS.CASH_OUT_OPTIONS);
  };
  
  // Handler for when user selects the widget option (when enabled)
  const handleSelectWidgetOption = () => {
    if (OFF_RAMP_WIDGET_ENABLED) {
      setStep(STEPS.SELECT_METHOD);
    }
  };

  const handleSelectProvider = (provider) => {
    if (!provider.available) return;
    setSelectedProvider(provider);
    setStep(STEPS.OFFRAMP_WIDGET);
  };

  const handleComplete = async () => {
    // Mark as claimed
    try {
      await fetch(`${BACKEND_URL}/api/mtcn/mark-claimed?wallet_address=${claimData.wallet_address}`, {
        method: 'POST'
      });
    } catch (e) {
      console.error('Failed to mark claimed:', e);
    }
    setStep(STEPS.SUCCESS);
  };

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  // Generate Transak widget URL
  const getTransakWidgetUrl = () => {
    const amount = claimData?.amount || 100;
    const params = new URLSearchParams({
      apiKey: TRANSAK_API_KEY,
      environment: 'PRODUCTION',
      cryptoCurrencyCode: 'USDC',
      network: 'polygon',
      defaultCryptoAmount: amount.toString(),
      walletAddress: claimData?.wallet_address || '',
      productsAvailed: 'SELL',
      themeColor: '059669'
    });
    
    return `https://global.transak.com?${params.toString()}`;
  };

  const getOffRampWidgetUrl = () => {
    return getTransakWidgetUrl();
  };
  
  // Calculate estimated local amount
  const estimatedLocalAmount = claimData?.amount ? getEstimatedAmount(claimData.amount) : null;

  // =========================================================================
  // RENDER
  // =========================================================================

  // Error state
  if (step === STEPS.ERROR) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center shadow-lg">
          <CardHeader>
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-red-700">Verification Failed</CardTitle>
            <CardDescription>{error || 'Invalid code or security answer'}</CardDescription>
          </CardHeader>
          <CardFooter className="flex-col gap-2">
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => {
                setStep(STEPS.ENTER_CODE);
                setError(null);
              }}
              data-testid="try-again-btn"
            >
              Try Again
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/')}
            >
              Go to Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Step 1: Enter Code
  if (step === STEPS.ENTER_CODE || step === STEPS.VERIFYING) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Claim Your Transfer</CardTitle>
            <CardDescription>
              Enter the code you received from the sender
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mnemonic" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-emerald-600" />
                Transfer Code (12 words)
              </Label>
              <Textarea
                id="mnemonic"
                placeholder="Enter your 12-word transfer code..."
                value={mnemonicInput}
                onChange={(e) => setMnemonicInput(e.target.value)}
                className="min-h-[100px] font-mono text-sm"
                disabled={loading}
                data-testid="mnemonic-input"
              />
              <p className="text-xs text-gray-500">
                Example: apple bread candy dog elephant fish grape honey ice jam king lemon
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="security" className="flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-600" />
                Security Answer (Last Name)
              </Label>
              <Input
                id="security"
                type="text"
                placeholder="Enter recipient's last name"
                value={securityAnswer}
                onChange={(e) => setSecurityAnswer(e.target.value)}
                className="h-12"
                disabled={loading}
                data-testid="security-input"
              />
            </div>
            
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            
            <Button 
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
              onClick={handleVerifyClaim}
              disabled={loading || !mnemonicInput.trim() || !securityAnswer.trim()}
              data-testid="verify-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify & Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </CardContent>
          
          <CardFooter className="flex-col space-y-2 text-center text-xs text-gray-500 pt-0">
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-emerald-600" />
                <span>Secure</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-emerald-600" />
                <span>Non-Custodial</span>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Step 2: Show Amount
  if (step === STEPS.SHOW_AMOUNT) {
    const actualBalance = claimData?.amount || 0;
    const hasFunds = actualBalance > 0;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <div className={`mx-auto w-20 h-20 ${hasFunds ? 'bg-emerald-100' : 'bg-amber-100'} rounded-full flex items-center justify-center mb-4`}>
              {hasFunds ? (
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              ) : (
                <AlertCircle className="w-10 h-10 text-amber-600" />
              )}
            </div>
            <CardTitle className="text-2xl">{hasFunds ? 'Verified!' : 'Code Verified'}</CardTitle>
            <CardDescription>
              {hasFunds 
                ? 'Your transfer is ready to withdraw' 
                : 'This wallet has no funds yet. Payment may still be processing.'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Amount Display */}
            <div className={`${hasFunds ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-gradient-to-br from-amber-400 to-orange-500'} rounded-2xl p-6 text-center text-white shadow-lg`}>
              <p className="text-sm opacity-80 mb-1">
                {hasFunds ? 'Available Balance' : 'Current Balance'}
              </p>
              <p className="text-4xl font-bold">
                {claimData?.currency || 'USD'} {actualBalance.toFixed(2)}
              </p>
              {!hasFunds && claimData?.intended_amount && (
                <p className="text-sm opacity-70 mt-2">
                  Expected: {claimData.currency || 'USD'} {claimData.intended_amount.toFixed(2)}
                </p>
              )}
            </div>
            
            {/* No funds warning */}
            {!hasFunds && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <p className="text-sm text-amber-800">
                  <strong>No funds detected.</strong> The sender may not have completed the payment yet. 
                  Please check back later or contact the sender.
                </p>
              </div>
            )}
            
            {/* Sender info */}
            {claimData?.sender_name && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">From</p>
                <p className="font-medium">{claimData.sender_name}</p>
                {claimData?.message && (
                  <p className="text-gray-600 italic mt-2">"{claimData.message}"</p>
                )}
              </div>
            )}
            
            {/* THE MAGIC BUTTON - only show if has funds */}
            {hasFunds ? (
              <Button 
                className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700 shadow-md"
                onClick={handleWithdrawToBank}
                data-testid="withdraw-btn"
              >
                <Banknote className="w-5 h-5 mr-2" />
                Withdraw Funds
              </Button>
            ) : (
              <Button 
                className="w-full h-14 text-lg"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                <Clock className="w-5 h-5 mr-2" />
                Refresh to Check Again
              </Button>
            )}
            
            {hasFunds && (
              <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>1-3 business days</span>
                </div>
                <div className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  <span>Secure transfer</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Cash Out Options (NEW - Dual option selection)
  if (step === STEPS.CASH_OUT_OPTIONS) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Banknote className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-xl">Cash Out Your Funds</CardTitle>
            <CardDescription>
              Choose how you want to receive your money
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <CashOutOptions
              claimData={claimData}
              userCountry={userCountry || 'US'}
              estimatedLocalAmount={estimatedLocalAmount}
              localCurrency={localCurrency || 'USD'}
              onSelectWidget={handleSelectWidgetOption}
              onBack={() => setStep(STEPS.SHOW_AMOUNT)}
              mnemonic={mnemonicInput}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 4: Select withdrawal method (Widget provider selection - when widget is enabled)
  if (step === STEPS.SELECT_METHOD) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <CardTitle>How do you want to receive?</CardTitle>
            <CardDescription>
              Choose how to withdraw {claimData?.currency || 'USD'} {claimData?.amount?.toFixed(2) || '100.00'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-3">
            {OFFRAMP_PROVIDERS.map((provider) => {
              const Icon = provider.icon;
              return (
                <button
                  key={provider.id}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    provider.available
                      ? 'border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 cursor-pointer'
                      : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                  } ${provider.recommended ? 'border-emerald-300 bg-emerald-50' : ''}`}
                  onClick={() => handleSelectProvider(provider)}
                  disabled={!provider.available}
                  data-testid={`provider-${provider.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        provider.recommended ? 'bg-emerald-200' : 'bg-emerald-100'
                      }`}>
                        <Icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-800">{provider.name}</p>
                          {provider.recommended && (
                            <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{provider.description}</p>
                        <p className="text-xs text-emerald-600">{provider.time}</p>
                      </div>
                    </div>
                    {provider.available && (
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>
              );
            })}
            
            <Button 
              variant="ghost" 
              className="w-full mt-4"
              onClick={() => setStep(STEPS.CASH_OUT_OPTIONS)}
            >
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 4: Off-ramp Widget
  if (step === STEPS.OFFRAMP_WIDGET) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg shadow-xl border-0">
          <CardHeader className="text-center">
            <CardTitle>Withdraw to Your Bank</CardTitle>
            <CardDescription>
              Complete your withdrawal of {claimData?.currency || 'USD'} {claimData?.amount?.toFixed(2) || '100.00'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Off-ramp Widget iframe */}
            <div className="rounded-xl overflow-hidden border border-gray-200 bg-white">
              <iframe
                src={getOffRampWidgetUrl()}
                title="Withdraw to Bank"
                allow="accelerometer; autoplay; camera; gyroscope; payment"
                className="w-full h-[500px] border-0"
                data-testid="offramp-widget"
              />
            </div>
            
            {/* Alternative: Open in new tab */}
            <div className="text-center">
              <button
                onClick={() => window.open(getOffRampWidgetUrl(), '_blank')}
                className="text-sm text-emerald-600 hover:underline flex items-center justify-center gap-1 mx-auto"
              >
                <ExternalLink className="w-4 h-4" />
                Open in new window
              </button>
            </div>
            
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={handleComplete}
              data-testid="complete-btn"
            >
              I've completed my withdrawal
            </Button>
            
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => setStep(STEPS.CASH_OUT_OPTIONS)}
            >
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success
  if (step === STEPS.SUCCESS) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Withdrawal Initiated!</CardTitle>
            <CardDescription>
              Your money is on its way to your bank
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount</span>
                <span className="font-semibold">{claimData?.currency || 'USD'} {claimData?.amount?.toFixed(2) || '100.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Method</span>
                <span className="font-semibold">{selectedProvider?.name || 'Bank Transfer'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Expected arrival</span>
                <span className="font-semibold">{selectedProvider?.time || '1-3 business days'}</span>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
              <p className="font-medium mb-1">What happens next?</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-600">
                <li>Your withdrawal is being processed</li>
                <li>You'll receive a confirmation email</li>
                <li>Money arrives in your bank in 1-3 days</li>
              </ol>
            </div>
            
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => navigate('/')}
              data-testid="done-btn"
            >
              Done
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
